import { createBrowserClient } from '@supabase/ssr';

// Simple cookie helpers — no Partitioned attribute, SameSite=Lax so the
// Next.js middleware (which reads request.cookies) can always see the token.
const parseCookies = (): { name: string; value: string }[] => {
  if (typeof document === 'undefined') return [];
  return document.cookie
    .split(';')
    .filter(Boolean)
    .map((c) => {
      const eqIdx = c.trim().indexOf('=');
      if (eqIdx < 0) return { name: c.trim(), value: '' };
      // Do NOT decode — Supabase SSR writes raw JSON; decoding here would
      // double-decode when the middleware reads request.cookies (which auto-decodes).
      return {
        name: c.trim().slice(0, eqIdx),
        value: c.trim().slice(eqIdx + 1),
      };
    })
    .filter((c) => c.name.length > 0);
};

const writeCookie = (name: string, value: string, options?: any) => {
  if (typeof document === 'undefined') return;
  // Do NOT encodeURIComponent — Supabase SSR stores the session as a raw JSON
  // string. Encoding it here causes the middleware's request.cookies parser to
  // receive a double-encoded value, which the @supabase/ssr JSON parser cannot
  // read, resulting in getUser() returning null and the middleware redirecting
  // the user back to the landing page after a successful login.
  //
  // Use SameSite=None; Secure when on HTTPS so cookies are sent in iframe
  // (cross-origin) contexts such as the Rocket preview. Fall back to
  // SameSite=Lax on plain HTTP (local dev).
  const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const sameSite = isHttps ? 'None' : 'Lax';
  let s = `${name}=${value}; Path=${options?.path ?? '/'}; SameSite=${sameSite}`;
  if (options?.maxAge) s += `; Max-Age=${options.maxAge}`;
  if (options?.expires) s += `; Expires=${new Date(options.expires).toUTCString()}`;
  if (isHttps) {
    s += '; Secure';
  }
  document.cookie = s;
};

const deleteCookie = (name: string) => {
  if (typeof document === 'undefined') return;
  const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const sameSite = isHttps ? 'None' : 'Lax';
  let s = `${name}=; Path=/; Max-Age=0; SameSite=${sameSite}`;
  if (isHttps) s += '; Secure';
  document.cookie = s;
};

// Inject the auth token into same-origin fetch requests so the middleware
// can pick it up via the x-sb-token header when cookies are unavailable.
const getAuthToken = (): string | null =>
  parseCookies().find((c) => c.name.includes('auth-token'))?.value ?? null;

if (typeof window !== 'undefined' && !(window as any).__sb_patched__) {
  (window as any).__sb_patched__ = true;
  const orig = window.fetch.bind(window);
  window.fetch = (input, init) => {
    const token = getAuthToken();
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
        ? input.href
        : (input as Request).url;
    if (token && (url.startsWith('/') || url.startsWith(window.location.origin))) {
      init = {
        ...(init ?? {}),
        headers: { ...(init?.headers ?? {}), 'x-sb-token': token },
      };
    }
    return orig(input, init);
  };
}

export function createClient() {
  return createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      cookies: {
        getAll: () => parseCookies(),
        setAll(cookiesToSet) {
          if (typeof document === 'undefined') return;
          cookiesToSet.forEach(({ name, value, options }) => {
            if (value) {
              writeCookie(name, value, options);
            } else {
              deleteCookie(name);
            }
          });
        },
      },
    }
  );
}
